/**
 * Advanced line fields (W2-06; DESIGN.md §4.11 "> Advanced (Full)", §5.5, rpe-format F§9.4, F§12, F§3.3):
 *  - Note tilt: a static number of degrees (Core.lines.setIncline writes one key at beat 0). Phira never advances
 *    the incline track, so an imported animated tilt is shown read-only with the value Phira really uses and a
 *    "Make static" fix (warning PH-18);
 *  - Distance effects (ControlsEditor);
 *  - RPE-only fields Phira ignores (BPM factor, anchor, skew control), read-only with "Phira ignores" badges on
 *    their own line under the value.
 */

import type { AppServices } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { Line, LineKind } from '../../../core/model/types';
import { useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { Badge, Button, NumberField } from '../../../ui';
import type { BadgeProps } from '../../../ui/types';
import { m } from '../inspector.messages';
import { inclineInfo, trackKeys } from '../model';
import { Callout, fmtNum, InfoTip, Row, WithInfo } from '../parts';
import s from '../Inspector.module.css';
import type { EditKit } from '../tx';
import { FIELD } from '../useFieldProblems';
import { ControlsEditor } from './ControlsEditor';

export interface LineAdvancedProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly edit: EditKit;
  readonly line: Line;
  readonly kind: LineKind;
  readonly badges: (field: string) => readonly BadgeProps[];
}

export function LineAdvanced(props: LineAdvancedProps) {
  const { store, edit, line } = props;
  const t = useT();
  const incline = inclineInfo(trackKeys(store.doc, store.idx, store.idx.track(line.id, 'incline', 0)));
  const rpe = line.rpe;
  const skew = rpe.skewControl === null ? t(m.skewDefault) : t(m.skewCustom);

  return (
    <div className={s.fields}>
      <Row edit={edit} field={FIELD.lineIncline}>
        <WithInfo info={{ text: m.inclineInfo, rpe: 'inclineEvents', community: '倾斜' }}>
          <NumberField
            label={t(m.incline)}
            value={{ kind: 'value', value: incline.frozen }}
            unit={t(m.deg)}
            step={1}
            precision={3}
            disabled={incline.animated}
            help={{ key: m.inclineHelp }}
            helpId={FIELD.lineIncline}
            badges={props.badges(FIELD.lineIncline)}
            onCommit={(v) => {
              if (v.kind !== 'number') return;
              const degrees = v.value;
              edit.commit(FIELD.lineIncline, { key: m.txIncline }, (tx) => {
                Core.lines.setIncline(tx, { line: line.id, degrees });
              });
            }}
            onScrub={(v, final) => {
              edit.scrub(
                FIELD.lineIncline,
                { key: m.txIncline },
                (tx) => {
                  Core.lines.setIncline(tx, { line: line.id, degrees: v });
                },
                final,
              );
            }}
          />
        </WithInfo>
        {incline.animated && (
          <Callout
            tone="warning"
            testId="incline-animated"
            actions={
              <Button
                size="sm"
                onClick={() => {
                  edit.once({ key: m.txIncline }, (tx) => {
                    Core.lines.setIncline(tx, { line: line.id, degrees: incline.frozen });
                  });
                }}
              >
                {t(m.makeStatic)}
              </Button>
            }
          >
            {t(m.inclineAnimated, { deg: fmtNum(incline.frozen, 3) })}
          </Callout>
        )}
      </Row>

      <div className={s.inline}>
        <span className={s.subheading}>{t(m.controls)}</span>
        <InfoTip text={m.controlsInfo} rpe="posControl / alphaControl / sizeControl / yControl" community="控制" />
      </div>
      <ControlsEditor services={props.services} edit={edit} line={line} badges={props.badges} />

      <div className={s.inline}>
        <span className={s.subheading}>{t(m.rpeOnly)}</span>
        <InfoTip text={m.rpeOnlyTip} />
      </div>
      <dl className={s.propList} data-testid="rpe-only">
        <RpeOnly
          label={t(m.bpmfactor)}
          value={fmtNum(rpe.bpmfactor, 4)}
          badges={props.badges(FIELD.lineBpmfactor)}
          field={FIELD.lineBpmfactor}
        />
        <RpeOnly
          label={t(m.anchor)}
          value={`${fmtNum(rpe.anchor[0], 3)} ${fmtNum(rpe.anchor[1], 3)}`}
          badges={props.badges(FIELD.lineAnchor)}
          field={FIELD.lineAnchor}
        />
        <RpeOnly
          label={t(m.skewControl)}
          value={skew}
          badges={props.badges(FIELD.lineSkewControl)}
          field={FIELD.lineSkewControl}
        />
      </dl>
    </div>
  );
}

function RpeOnly(props: {
  readonly label: string;
  readonly value: string;
  readonly field: string;
  readonly badges: readonly BadgeProps[];
}) {
  return (
    <div className={s.rpeOnlyItem} data-help-id={props.field}>
      <dt className={s.propName}>{props.label}</dt>
      <dd className={s.propState}>
        <span className={s.mono}>{props.value}</span>
      </dd>
      {/* Badges on their own line: beside the value they squeezed it to one character per line (UX-060). */}
      <dd className={s.rpeOnlyBadges}>
        <Badge kind="phiraIgnores" />
        {props.badges.map((b, i) => (
          <Badge key={i} {...b} />
        ))}
      </dd>
    </div>
  );
}
